import React, { useState, useMemo, useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import { auth, churchConfig as configApi } from '../api/endpoints';
import { LANGUAGES, LANGUAGE_NAMES, DEFAULT_LANGUAGE, isLanguageName } from '../utils/languages';
import type { LanguageName } from '../utils/languages';
// The login screen always shows the bundled product image, never a client's
// configured logo — it must look identical for every deployment and render
// before sign-in, with the server down or Settings untouched.
import churchLogo from '../church.png';


const Login = ({ onLogin }) => {
  const [credentials, setCredentials] = useState<{ language: LanguageName; username: string; password: string }>({
    language: DEFAULT_LANGUAGE,
    username: '',
    password: ''
  });
  // Settings can pick the language the screen opens in. It is applied only
  // until the user touches the selector, so a slow answer never overrides a
  // choice already made.
  const languageTouched = useRef(false);
  useEffect(() => {
    let active = true;
    configApi.publicBranding()
      .then((branding) => {
        if (!active || languageTouched.current) return;
        const preferred = branding?.defaultLanguage;
        if (isLanguageName(preferred)) setCredentials((prev) => ({ ...prev, language: preferred }));
      })
      .catch(() => { /* offline or unconfigured — English stays */ });
    return () => { active = false; };
  }, []);
  const [errorMsg, setErrorMsg] = useState('');
  // Off by default: a password should not be readable over the user's shoulder
  // unless they ask for it.
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const t = LANGUAGES[credentials.language];

  // Fixed per mount so the sky doesn't re-scatter on every keystroke.
  const stars = useMemo(() => {
    const starArray = [];
    for (let i = 0; i < 3; i += 1) {
      starArray.push({
        id: `big-${i}`,
        left: `${Math.random() * 85 + 5}vw`, top: `${Math.random() * 40 + 5}vh`,
        size: `${Math.random() * 3 + 6}px`,
        animationDuration: `${Math.random() * 3 + 3}s`,
        animationDelay: `${Math.random() * 2}s`,
        opacity: 1, isBig: true
      });
    }
    for (let i = 0; i < 120; i += 1) {
      starArray.push({
        id: `small-${i}`,
        left: `${Math.random() * 100}vw`, top: `${Math.random() * 100}vh`,
        size: `${Math.random() * 2 + 1}px`,
        animationDuration: `${Math.random() * 4 + 2}s`,
        animationDelay: `${Math.random() * 5}s`,
        opacity: Math.random() * 0.5 + 0.2, isBig: false
      });
    }
    return starArray;
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prevState => ({
      ...prevState,
      [name]: value
    }));
    if (name === 'language') languageTouched.current = true;
    if (errorMsg && name !== 'language') setErrorMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setIsLoading(true);

    try {
      const data = await auth.login(credentials.username, credentials.password);

      if (data?.token && data?.user) {
        data.user.parsedPermissions = data.user.permissions ? JSON.parse(data.user.permissions) : {};
        
        // Pass User Data, Language, AND the JWT Token up to App.js
        onLogin(data.user, t.code, data.token);
      } else {
        setErrorMsg(t.err);
      }
    } catch (err) {
      // A lockout says how long to wait; anything else stays deliberately vague.
      setErrorMsg(err?.status === 429 && err.message
        ? err.message
        : 'Invalid username or password, or server is offline.');
    }
    setIsLoading(false);
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes twinkleBig {
          0%   { transform: scale(0.9); opacity: 0.8; box-shadow: 0 0 10px #FFF, 0 0 20px #FFF; }
          50%  { transform: scale(1.2); opacity: 1;   box-shadow: 0 0 15px #FFF, 0 0 35px #E0F7FA; }
          100% { transform: scale(0.9); opacity: 0.8; box-shadow: 0 0 10px #FFF, 0 0 20px #FFF; }
        }
        @keyframes twinkleSmall {
          0%   { opacity: 0.1; transform: scale(0.8); }
          50%  { opacity: 1;   transform: scale(1.1); box-shadow: 0 0 4px #FFF; }
          100% { opacity: 0.1; transform: scale(0.8); }
        }

        .login-card {
          display: flex;
          flex-direction: row;
          width: 90%;
          max-width: 850px;
          background-color: #FFFFFF;
          border-radius: 12px;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.5);
          position: relative;
          z-index: 1;
          border-top: 6px solid #6B4277;
        }
        .login-left {
          flex: 1;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 40px;
          border-right: 1px solid #EAEAEA;
          background-color: #FAFAFA;
          border-top-left-radius: 12px;
          border-bottom-left-radius: 12px;
        }
        .login-left img {
          width: 100%;
          height: auto;
          max-height: 400px;
          object-fit: contain;
        }
        .login-right {
          flex: 1;
          padding: 40px;
          text-align: center;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        /* On a phone the card stacks, and the logo panel becomes a compact
           band above the form. It is the only logo on the screen now, so it
           cannot be dropped the way it used to be. */
        @media (max-width: 768px) {
          .login-card { flex-direction: column; max-width: 400px; }
          .login-left {
            padding: 24px;
            border-right: none;
            border-bottom: 1px solid #EAEAEA;
            border-radius: 12px 12px 0 0;
          }
          .login-left img { max-height: 150px; }
        }
      `}</style>

      {stars.map((star) => (
        <div
          key={star.id}
          style={{
            position: 'absolute', left: star.left, top: star.top,
            width: star.size, height: star.size,
            backgroundColor: '#FFF', borderRadius: '50%',
            opacity: star.opacity,
            animation: `${star.isBig ? 'twinkleBig' : 'twinkleSmall'} ${star.animationDuration} infinite ease-in-out ${star.animationDelay}`,
            pointerEvents: 'none', zIndex: 0
          }}
        />
      ))}

      <div className="login-card">
        <div className="login-left">
          <img src={churchLogo} alt="Church Management Software" className="notranslate" translate="no" />
        </div>

        <div className="login-right">
          <h2 style={styles.title}>{t.title}</h2>

          {errorMsg && <div style={styles.error}>{errorMsg}</div>}

          <form onSubmit={handleSubmit} style={styles.form}>

          <div style={styles.inputGroup}>
            <label style={styles.label}>{t.lang}</label>
            <select
              name="language"
              value={credentials.language}
              onChange={handleChange}
              style={styles.input}
            >
              {LANGUAGE_NAMES.map(lang => (
                <option key={lang} value={lang}>
                  {LANGUAGES[lang].nativeName}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>{t.user}</label>
            <input
              type="text"
              name="username"
              value={credentials.username}
              onChange={handleChange}
              required
              style={styles.input}
              placeholder={t.user}
            />
          </div>
          
          <div style={styles.inputGroup}>
            <label style={styles.label}>{t.pass}</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={credentials.password}
                onChange={handleChange}
                required
                // Room on the right so the longest password never runs under
                // the toggle.
                style={{ ...styles.input, paddingRight: '46px' }}
                placeholder={t.pass}
              />
              <button
                type="button"
                onClick={() => setShowPassword((on) => !on)}
                // Named for what pressing it does, and marked as a state so a
                // screen reader announces whether the password is showing.
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                title={showPassword ? 'Hide password' : 'Show password'}
                // Skipped in the tab order: tabbing from the password box should
                // reach Sign In, not a decoration.
                tabIndex={-1}
                style={{
                  position: 'absolute', top: 0, right: 0, height: '100%', width: '44px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '17px', lineHeight: 1, color: '#8A7A6D', padding: 0
                }}
              >
                <span className="notranslate" translate="no" aria-hidden="true">
                  {showPassword ? '🙈' : '👁️'}
                </span>
              </button>
            </div>
          </div>
          
            <button type="submit" disabled={isLoading} style={styles.button}>
              {isLoading ? '...' : t.btn}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Night-sky login. The card itself stays light; the starfield sits behind it.
const styles: Record<string, CSSProperties> = {
  container: {
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    width: '100vw',
    backgroundColor: '#050B14',
    background: 'linear-gradient(to bottom, #02040A 0%, #0A192F 50%, #112240 100%)',
  },
  title: {
    marginTop: '0',
    marginBottom: '24px',
    color: '#3E2C22',
    fontSize: '26px',
    fontWeight: '800',
  },
  error: {
    backgroundColor: '#FDECEF',
    color: '#8A1C1C',
    padding: '12px',
    borderRadius: '4px',
    marginBottom: '20px',
    fontSize: '14px',
    textAlign: 'center',
    border: '1px solid #E5BCC2',
    fontWeight: 'bold',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left',
  },
  inputGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    color: '#5C4A3D',
    fontSize: '13px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid #D4CCC4',
    borderRadius: '6px',
    fontSize: '15px',
    color: '#3E2C22',
    backgroundColor: '#FAFAF9',
    boxSizing: 'border-box',
    transition: 'all 0.2s ease',
  },
  button: {
    backgroundColor: '#6B4277',
    color: '#FFFFFF',
    padding: '14px',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '10px',
    transition: 'background-color 0.2s',
    letterSpacing: '0.5px',
    boxShadow: '0 4px 10px rgba(107, 66, 119, 0.3)',
  }
};

export default Login;